import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import { AggregationPeriod } from "../../../utils/aggregationUtils";

interface AggregationSummaryTableProps {
  periods: AggregationPeriod[];
  metricUnit: string;
  aggregationType: "weekly" | "monthly" | "yearly";
}

const MAX_VISIBLE_ROWS = 20;
const ROW_HEIGHT = 37; // pixels per row
const TABLE_MAX_HEIGHT = `${MAX_VISIBLE_ROWS * ROW_HEIGHT}px`;

const AggregationSummaryTable: React.FC<AggregationSummaryTableProps> = ({
  periods,
  metricUnit,
  aggregationType,
}) => {
  if (periods.length === 0) {
    return (
      <Paper
        sx={{
          p: 2,
          mt: 3,
          backgroundColor: "#fafafa",
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="textSecondary">
          No aggregation data available.
        </Typography>
      </Paper>
    );
  }

  const getAggregationLabel = () => {
    switch (aggregationType) {
      case "weekly":
        return "Weekly";
      case "monthly":
        return "Monthly";
      case "yearly":
        return "Yearly";
      default:
        return "Aggregation";
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {getAggregationLabel()} Summary
        </Typography>

        <Box
          sx={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: TABLE_MAX_HEIGHT,
          }}
        >
          <Table
            size="small"
            sx={{
              minWidth: 800,
              "& th": {
                backgroundColor: "#f5f5f5",
                fontWeight: 700,
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
              "& td": {
                padding: "8px 12px",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="center">Start Date</TableCell>
                <TableCell align="center">End Date</TableCell>
                <TableCell align="center">Days with Data</TableCell>
                <TableCell align="center">Missing Days</TableCell>
                <TableCell align="center">Coverage %</TableCell>
                <TableCell align="right">Min {metricUnit}</TableCell>
                <TableCell align="right">Max {metricUnit}</TableCell>
                <TableCell align="right">Mean {metricUnit}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map((period, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? "#fafafa" : "white",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{period.label}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                    {period.startDate}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                    {period.endDate}
                  </TableCell>
                  <TableCell align="center">{period.daysWithData}</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color:
                        period.missingDates.length > 0 ? "#cc0000" : "#008000",
                      fontWeight: 600,
                    }}
                  >
                    {period.missingDates.length}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "inline-block",
                        padding: "2px 6px",
                        backgroundColor:
                          period.coveragePct >= 80
                            ? "#e8f5e9"
                            : period.coveragePct >= 50
                              ? "#fff3e0"
                              : "#ffebee",
                        borderRadius: "4px",
                        color:
                          period.coveragePct >= 80
                            ? "#2e7d32"
                            : period.coveragePct >= 50
                              ? "#e65100"
                              : "#c62828",
                        fontWeight: 600,
                      }}
                    >
                      {period.coveragePct.toFixed(1)}%
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.85rem" }}>
                    {period.minValue !== null
                      ? period.minValue.toFixed(2)
                      : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.85rem" }}>
                    {period.maxValue !== null
                      ? period.maxValue.toFixed(2)
                      : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.85rem" }}>
                    {period.meanValue !== null
                      ? period.meanValue.toFixed(2)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
};

export default AggregationSummaryTable;
